/**
 * English translations for registration wizard.
 */

const registration = {
  // Page header
  title: 'Register',
  subtitle: 'Create your Safaqat24 account',

  // User type selection
  userType: {
    title: 'What do you want to register as?',
    subtitle: 'Pick what fits you - list your own property as an individual, or register as a licensed broker or a real-estate company.',
    individual: {
      title: 'Individual',
      description: 'List your property for sale or rent through Safaqat24',
    },
    broker: {
      title: 'Licensed Broker',
      description: 'Real estate professional with FAL licence',
    },
    company: {
      title: 'Company',
      description: 'Real estate company with FAL licence and CR number',
    },
    developer: {
      title: 'Real Estate Developer',
      description: 'Developer marketing projects and compounds, with FAL licence',
    },
  },

  // Step labels
  steps: {
    userType: 'Account Type',
    account: 'Account',
    emailVerify: 'Verify Email',
    businessInfo: 'Business Info',
    propertyInfo: 'Property Info',
    contact: 'Contact',
    subscription: 'Subscription',
    payment: 'Payment',
    review: 'Review',
  },

  // Email verification step
  emailVerify: {
    title: 'Verify Your Email',
    subtitle: 'We sent a 6-digit code to',
    enterCode: 'Enter verification code',
    verify: 'Verify',
    resend: 'Resend code',
    resendIn: 'Resend in {{seconds}}s',
    codeSent: 'Verification code sent!',
    invalidCode: 'Invalid code. Please try again.',
    codeExpired: 'Code expired. Please resend.',
    differentEmail: 'Use different email',
  },

  // Account creation
  account: {
    title: 'Create Your Account',
    subtitle: 'Enter your details to get started',
    // Optional demographics (Statistics platform)
    gender: 'Gender',
    genderSelect: 'Select (optional)',
    genderMale: 'Male',
    genderFemale: 'Female',
    genderUndisclosed: 'Prefer not to say',
    nationality: 'Nationality',
    nationalitySelect: 'Select (optional)',
    birthYear: 'Year of Birth',
    birthYearSelect: 'Select (optional)',
    demographicsNote: 'Optional - helps us understand our audience. You can change or remove this anytime in your account settings.',
    firstName: 'First Name',
    firstNamePlaceholder: 'John',
    lastName: 'Last Name',
    lastNamePlaceholder: 'Doe',
    email: 'Email Address',
    emailPlaceholder: 'you@example.com',
    phone: 'Phone Number',
    phonePlaceholder: '+966 5XX XXX XXXX',
    password: 'Password',
    passwordPlaceholder: 'At least 8 characters',
    confirmPassword: 'Confirm Password',
    confirmPasswordPlaceholder: 'Re-enter your password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    errors: {
      firstNameRequired: 'First name is required',
      lastNameRequired: 'Last name is required',
      emailRequired: 'Email is required',
      emailInvalid: 'Please enter a valid email',
      phoneRequired: 'Phone number is required',
      phoneInvalid: 'Please enter a valid phone number',
      passwordRequired: 'Password is required',
      passwordLength: 'Password must be at least 8 characters',
      confirmPasswordRequired: 'Please confirm your password',
      passwordMismatch: 'Passwords do not match',
    },
  },

  // Business info form
  businessInfo: {
    title: 'Business Information',
    subtitle: 'Tell us about your business',
    companyName: 'Company Name',
    companyNameAr: 'Company Name (Arabic)',
    companyNamePlaceholder: 'Enter company name',
    crNumber: 'Commercial Registration (CR)',
    crNumberPlaceholder: 'Enter CR number',
    crNumberHelp: '10-digit number from Ministry of Commerce',
    falLicense: 'FAL Licence Number',
    falLicensePlaceholder: 'Enter FAL licence number',
    falLicenseHelp: 'Required for real estate activities in Saudi Arabia',
    phone: 'Business Phone',
    phonePlaceholder: '05xxxxxxxx',
    email: 'Business Email',
    emailPlaceholder: 'contact@company.com',
    address: 'Business Address',
    city: 'City',
    cityPlaceholder: 'Select city',
    district: 'District',
    districtPlaceholder: 'Enter district',
    fullAddress: 'Full Address',
    fullAddressPlaceholder: 'Street name, building number...',
    logo: 'Company Logo',
    logoHelp: 'Upload your company logo (PNG, JPG, max 5MB)',
    uploadLogo: 'Upload Logo',
    changeLogo: 'Change Logo',
    removeLogo: 'Remove',
  },

  // Subscription selection
  subscription: {
    title: 'Choose Your Plan',
    subtitle: 'Select the plan that fits your needs',
    monthly: 'Monthly',
    yearly: 'Yearly',
    yearlyDiscount: 'Save {{percent}}%',
    perMonth: '/month',
    perYear: '/year',
    mostPopular: 'Most Popular',
    features: {
      listings: '{{count}} listings',
      teamMembers: '{{count}} team members',
      featuredListings: '{{count}} featured listings',
      prioritySupport: 'Priority support',
      analytics: 'Advanced analytics',
      apiAccess: 'API access',
    },
    selectPlan: 'Select Plan',
    selected: 'Selected',
    currentPlan: 'Current Plan',
    skipForNow: 'Skip for now',
    skipNote: 'You can choose a plan later from the Subscriptions page',
  },

  // Promo code
  promo: {
    title: 'Promo Code',
    placeholder: 'Enter promo code',
    apply: 'Apply',
    applying: 'Applying...',
    remove: 'Remove',
    valid: 'Promo code applied!',
    invalid: 'Invalid promo code',
    expired: 'This promo code has expired',
    limitReached: 'This promo code has reached its usage limit',
    discount: 'Discount: {{amount}} SAR',
    percentOff: '{{percent}}% off',
    freeMonths: '{{count}} month(s) free',
  },

  // Payment
  payment: {
    title: 'Payment',
    subtitle: 'Complete your subscription',
    summary: 'Order Summary',
    plan: 'Plan',
    billing: 'Billing',
    subtotal: 'Subtotal',
    discount: 'Discount',
    total: 'Total',
    securePayment: 'Secure payment via Moyasar',
    methods: {
      cards: 'Cards',
      mada: 'MADA',
      applePay: 'Apple Pay',
    },
    processing: 'Processing payment...',
    success: 'Payment successful!',
    failed: 'Payment failed',
    tryAgain: 'Try again',
  },

  // Review step
  review: {
    title: 'Review & Confirm',
    subtitle: 'Please review your information',
    businessDetails: 'Business Details',
    subscriptionDetails: 'Subscription Details',
    paymentDetails: 'Payment Details',
    edit: 'Edit',
    confirm: 'Confirm & Submit',
    submitting: 'Submitting...',
    termsText: 'By clicking confirm, you agree to our',
    termsLink: 'Terms of Service',
    andText: 'and',
    privacyLink: 'Privacy Policy',
  },

  // FAL Disclaimer
  fal: {
    title: 'FAL Licence Disclaimer',
    subtitle: 'Important information about listing your property',
    body1: 'Your property will be listed under Safaqat24\'s REGA-issued FAL real-estate brokerage licence. Safaqat24 acts as the facilitating broker between you and potential buyers or tenants.',
    body2: 'A 2.5% commission (plus 15% VAT) will apply on successful transactions. You retain full ownership rights throughout the entire process.',
    body3: 'Once approved, your property will be advertised under Safaqat24\'s REGA accreditation and will display a valid advertising-licence number, as required by the General Authority for Real Estate (REGA).',
    howItWorks: 'How It Works',
    step1: 'Submit your property details through our form',
    step2: 'Our team reviews and approves your listing',
    step3: 'Property is listed on Safaqat24 under our FAL licence',
    step4: 'We handle enquiries and facilitate the transaction',
    checkbox: 'I understand and agree to list my property under Safaqat24\'s FAL licence',
    continue: 'Continue to Property Form',
    back: 'Go Back',
  },

  // Individual flow
  individual: {
    title: 'List Your Property',
    subtitle: 'Submit your property to be listed by Safaqat24',
    propertyType: 'Property Type',
    propertyTypePlaceholder: 'Select property type',
    listingType: 'Listing Type',
    forSale: 'For Sale',
    forRent: 'For Rent',
    description: 'Property Description',
    descriptionPlaceholder: 'Describe your property...',
    descriptionHelp: 'Include details about bedrooms, bathrooms, area, features',
    expectedPrice: 'Expected Price',
    expectedPricePlaceholder: 'Enter expected price in SAR',
    location: 'Property Location',
    contact: 'Contact Information',
    contactName: 'Your Name',
    contactPhone: 'Phone Number',
    contactEmail: 'Email (optional)',
    howItWorks: 'How It Works',
    step1: 'Submit your property details',
    step2: 'Our team reviews and contacts you',
    step3: 'Property listed under Safaqat24 FAL',
    step4: 'Pay commission only on successful sale/rent',
    commission: 'Commission: {{rate}}% (plus 15% VAT) on successful transaction',
  },

  // Success screens
  success: {
    title: 'Welcome to Safaqat24!',
    subtitle: 'Your registration is complete',
    accountCreated: 'Account Created',
    companyCreated: 'Company Created',
    profileCreated: 'Profile Created',
    subscriptionActive: 'Subscription Active',
    whatNext: "What's Next?",
    completeProfile: 'Complete Your Profile',
    inviteTeam: 'Invite Team Members',
    addListing: 'Add Your First Listing',
    goDashboard: 'Go to Dashboard',
    skipOnboarding: 'Skip for now',
  },

  individualSuccess: {
    title: 'Account Created Successfully!',
    subtitle: 'You can now list your property on Safaqat24',
    requestId: 'Request ID',
    status: 'Status',
    pending: 'Pending Review',
    nextSteps: 'What Happens Next?',
    step1: 'Click "List Your Property" to submit your property details',
    step2: 'Accept the FAL licence disclaimer',
    step3: 'Fill in property information and submit for review',
    trackStatus: 'Track Your Request',
    listProperty: 'List Your Property',
    goHome: 'Go to Home',
  },

  // Validation messages
  validation: {
    required: 'This field is required',
    invalidEmail: 'Please enter a valid email',
    invalidPhone: 'Please enter a valid phone number',
    invalidCR: 'Please enter a valid CR number',
    invalidFAL: 'Please enter a valid FAL licence',
    selectPlan: 'Please select a subscription plan',
    selectType: 'Please select a property type',
    selectListingType: 'Please select sale or rent',
    minDescription: 'Description must be at least 50 characters',
  },

  // Action buttons
  buttons: {
    continue: 'Continue',
    back: 'Back',
    submit: 'Submit',
    cancel: 'Cancel',
    saveDraft: 'Save & Exit',
    loading: 'Loading…',
  },

  // Draft recovery
  draft: {
    found: 'Continue where you left off?',
    continue: 'Continue Registration',
    startOver: 'Start Over',
  },

  // Individual property request page
  individualRequest: {
    pageTitle: 'Submit Property Request',
    pageSubtitle: "Fill in your property details and we'll list it under Safaqat24's FAL licence",
    submitSuccess: 'Your property request has been submitted successfully! Our team will review it shortly.',
    submitError: 'Failed to submit your request. Please try again.',
    submitting: 'Submitting your request...',
  },

  // Navigation
  navigation: {
    back: 'Back',
    continue: 'Continue',
    submit: 'Submit Request',
  },

  // FAL status display in account/profile settings
  falStatus: {
    title: 'FAL Licence Verification',
    verified: 'Your FAL licence is verified',
    pending: 'Your FAL licence is under review',
    pendingNote: 'You will be notified when it is verified',
    rejected: 'Your FAL licence was rejected',
    rejectionReason: 'Reason',
    licenseNumber: 'FAL Licence Number',
    crNumber: 'Commercial Registration Number',
    resubmit: 'Re-submit for Review',
    resubmitSuccess: 'FAL licence re-submitted for review',
    updateLicense: 'Update Licence Number',
    submitting: 'Submitting...',
    submitError: 'Failed to re-submit. Please try again.',
  },

  // Intent-first sign-up (components/registration/intent/IntentSignupFlow.tsx):
  // the marketer choice and the step indicator above every screen.
  intent: {
    marketer: 'Real-estate marketer',
    stepperLabel: 'Sign-up steps',
    steps: {
      intent: 'Account type',
      roles: 'Activity',
      company: 'Company details',
      account: 'Your details',
      verify: 'Verify email',
      pro: 'Professional verification',
    },
  },
};

export default registration;
